/**
 * Date and Time Utilities
 * 
 * ZERO CONVERSION RULE: Backend stores naive IST datetimes.
 * Frontend treats all dates as local time. No UTC conversion, no 'Z' appending,
 * no timeZone offsets. Parse with new Date(str) and display with local methods.
 */

const LOCALE = 'en-IN';

/**
 * Format a Date object (or parseable string) as a local ISO string (no Z suffix).
 * Use this instead of .toISOString() when sending dates to the backend.
 */
export const toLocalISOString = (input) => {
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

/**
 * Format a Date as YYYY-MM-DD in local time (no UTC shift).
 */
export const toLocalDateString = (input) => {
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * Get current local datetime-local value (for min attributes on inputs).
 */
export const nowLocalDatetimeString = () => {
  return toLocalISOString(new Date())?.slice(0, 16) || '';
};

/**
 * Format date to Indian locale display
 */
export const formatDateIST = (dateString) => {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleDateString(LOCALE);
  } catch {
    return '';
  }
};

/**
 * Format time to 12-hour format
 */
export const formatTimeIST = (dateString) => {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleTimeString(LOCALE, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return '';
  }
};

/**
 * Format datetime to Indian locale
 */
export const formatDateTimeIST = (dateString) => {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleString(LOCALE, { hour12: true });
  } catch {
    return '';
  }
};

/**
 * Format date with full details
 */
export const formatDateLongIST = (dateString) => {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleDateString(LOCALE, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  } catch {
    return '';
  }
};

/**
 * Format date and time separately
 */
export const formatDateAndTimeIST = (dateString) => {
  if (!dateString) return { date: '', time: '' };
  try {
    return {
      date: formatDateIST(dateString),
      time: formatTimeIST(dateString)
    };
  } catch {
    return { date: '', time: '' };
  }
};
